import { NodeResizer, Position } from '@xyflow/react';
import { type ReactNode } from 'react';

import { ConnectionHandles } from '@/components/diagrams/components/ConnectionHandles/ConnectionHandles';
import { Handle } from '@/components/diagrams/components/Handle';
import {
  NODE_RESIZER_HANDLE_STYLE,
  NODE_RESIZER_LINE_STYLE,
} from '@/components/diagrams/FlowChart/nodes/nodeResizerStyles';
import { type TileNodeProps } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';

interface DiagramNodeFrameProps
  extends Pick<
    TileNodeProps,
    'id' | 'selected' | 'isConnectable' | 'dragging'
  > {
  /** True keeps the ratio of the width to the height while the user resizes. */
  keepAspectRatio?: boolean;
  children: ReactNode;
}

/**
 * The editing frame of a builder node that the shared tile does not draw,
 * such as standalone text or a shape. The user resizes a selected node. A
 * hidden source and target handle on each side anchor the saved
 * connections, as on the shared tile. The connection handles start new
 * connections while the canvas accepts them.
 */
export function DiagramNodeFrame({
  id,
  selected,
  isConnectable,
  dragging,
  keepAspectRatio,
  children,
}: DiagramNodeFrameProps) {
  return (
    <>
      <NodeResizer
        nodeId={id}
        lineStyle={NODE_RESIZER_LINE_STYLE}
        handleStyle={NODE_RESIZER_HANDLE_STYLE}
        isVisible={!!selected && isConnectable && !dragging}
        keepAspectRatio={keepAspectRatio}
      />
      {children}
      {Object.values(Position).flatMap((side) =>
        (['source', 'target'] as const).map((type) => (
          <Handle
            key={`${type}-${side}`}
            id={side}
            type={type}
            position={side}
            isConnectable={false}
            hidden
          />
        ))
      )}
      {isConnectable && <ConnectionHandles nodeId={id} />}
    </>
  );
}
